import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  Select,
  Switch,
  Badge,
  Tag,
  TagLabel,
  TagCloseButton,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Wrap,
  WrapItem,
  Alert,
  AlertIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Avatar,
  Image,
  useToast,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorMode,
  Card,
  CardBody,
  Divider,
  Flex,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '@/contexts/ApiContext';
import {
  CordeCommonModelsDtoUserprofileUserProfile as UserProfileDto,
  CordeCommonModelsDtoContactContact as ContactDto,
  CordeCommonModelsDtoUserprofileAvatar as AvatarDto,
  CordeCommonModelsDtoUserprofileUpdateUserProfileRequest as UpdateUserProfileRequest,
  CordeCommonModelsDtoUserprofileCreateUserProfileRequest as CreateUserProfileRequest,
} from '@/api/generated/v1/api';
import { decodeJWT } from '@/utils/jwt';
import { useContacts, ContactFormData } from '@/hooks/useContacts';
import { ContactForm } from '@/components/contacts/ContactForm';
import { CachedAvatar } from '@/components/common/CachedAvatar';

// Иконки
const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,7H13V9H11V7M11,11H13V17H11V11Z" />
  </svg>
);

const AddIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

const CameraIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 15.2c1.65 0 3-1.35 3-3s-1.35-3-3-3-3 1.35-3 3 1.35 3 3 3zm0-4.4c.77 0 1.4.63 1.4 1.4s-.63 1.4-1.4 1.4-1.4-.63-1.4-1.4.63-1.4 1.4-1.4z"/>
    <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
  </svg>
);

interface ProfileContact {
  type: string;
  value: string;
  label?: string;
}

interface NotificationSettings {
  email_notifications: boolean;
  push_notifications: boolean;
  sms_notifications: boolean;
  marketing_emails: boolean;
  security_alerts: boolean;
}

interface PrivacySettings {
  profile_visibility: string;
  show_email: boolean;
  show_phone: boolean;
  show_location: boolean;
  show_birth_date: boolean;
}

interface UISettings {
  theme: string;
  language: string;
  timezone: string;
  date_format: string;
  time_format: string;
}

interface ProfileFormData {
  // Основная информация
  first_name: string;
  middle_name: string;
  last_name: string;
  display_name: string;
  bio: string;
  date_of_birth: string;
  gender: string;
  location: string;
  language: string;
  timezone: string;
  
  // Контакты
  contacts: ProfileContact[];
  
  // Настройки
  notification_settings: NotificationSettings;
  privacy_settings: PrivacySettings;
  ui_settings: UISettings;
  
  // Метаданные
  metadata: { [key: string]: string };
}

interface ValidationErrors {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  bio?: string;
  date_of_birth?: string;
  contacts?: string[];
  general?: string;
}

const getContactTypes = (t: any) => [
  { value: 'email', label: t('profile.contactTypes.email') },
  { value: 'phone', label: t('profile.contactTypes.phone') },
  { value: 'telegram', label: t('profile.contactTypes.telegram') },
  { value: 'discord', label: t('profile.contactTypes.discord') },
  { value: 'website', label: t('profile.contactTypes.website') },
  { value: 'linkedin', label: t('profile.contactTypes.linkedin') },
  { value: 'twitter', label: t('profile.contactTypes.twitter') },
  { value: 'github', label: t('profile.contactTypes.github') },
];

const getGenderOptions = (t: any) => [
  { value: '', label: t('profile.genderOptions.notSpecified') },
  { value: 'male', label: t('profile.genderOptions.male') },
  { value: 'female', label: t('profile.genderOptions.female') },
  { value: 'other', label: t('profile.genderOptions.other') },
  { value: 'prefer_not_to_say', label: t('profile.genderOptions.preferNotToSay') },
];

const getPrivacyOptions = (t: any) => [
  { value: 'public', label: t('profile.privacyOptions.public') },
  { value: 'friends', label: t('profile.privacyOptions.friends') },
  { value: 'private', label: t('profile.privacyOptions.private') },
];

export const CreateEditProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const { profileId } = useParams();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isOpen: isAvatarModalOpen, onOpen: onAvatarModalOpen, onClose: onAvatarModalClose } = useDisclosure();
  const { colorMode, toggleColorMode } = useColorMode();

  // Отладочная информация для JWT токена
  React.useEffect(() => {
    console.log('CreateEditProfilePage mounted with:', {
      profileId,
      accessToken: accessToken ? 'present' : 'missing',
      isEditMode: Boolean(profileId)
    });
  }, [profileId, accessToken]);

  const { colorSchemes } = useThemeContext();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [originalContacts, setOriginalContacts] = useState<ContactDto[]>([]);
  const [activeAvatar, setActiveAvatar] = useState<AvatarDto | null>(null);

  // Contact management hook
  const { listContacts } = useContacts();

  // Создаем стабильную ссылку на entity для ContactForm
  const contactEntity = React.useMemo(() => ({
    type: 'userprofile' as const,
    id: profileId || '',
  }), [profileId]);

  // Metadata form state
  const [metadataKey, setMetadataKey] = useState('');
  const [metadataValue, setMetadataValue] = useState('');

  // Main form data
  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: '',
    middle_name: '',
    last_name: '',
    display_name: '',
    bio: '',
    date_of_birth: '',
    gender: '',
    location: '',
    language: 'ru',
    timezone: 'UTC',
    contacts: [],
    notification_settings: {
      email_notifications: true,
      push_notifications: true,
      sms_notifications: false,
      marketing_emails: false,
      security_alerts: true,
    },
    privacy_settings: {
      profile_visibility: 'public',
      show_email: false,
      show_phone: false,
      show_location: true,
      show_birth_date: false,
    },
    ui_settings: {
      theme: 'auto',
      language: 'ru',
      timezone: 'UTC',
      date_format: 'DD.MM.YYYY',
      time_format: '24h',
    },
    metadata: {},
  });

  const contactTypes = getContactTypes(t);
  const genderOptions = getGenderOptions(t);
  const privacyOptions = getPrivacyOptions(t);

  const isEditMode = Boolean(profileId);

  // Улучшенное определение userId из JWT токена
  const userId = React.useMemo(() => {
    if (!accessToken) return undefined;
    try {
      const decodedToken = decodeJWT(accessToken);
      console.log('Decoded JWT token:', decodedToken);
      return decodedToken?.sub || decodedToken?.user_id || decodedToken?.id || undefined;
    } catch (error) {
      console.error('Failed to decode JWT token:', error);
      return undefined;
    }
  }, [accessToken]);

  const api = useApi();

  // Load profile data if editing
  useEffect(() => {
    if (isEditMode && profileId) {
      loadProfile(profileId);
    }
  }, [isEditMode, profileId]);

  // Handle URL hash for direct tab navigation
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#contacts') {
      setActiveTab(1); // Contacts tab is index 1
    } else if (hash === '#settings') {
      setActiveTab(2); // Settings tab is index 2  
    } else if (hash === '#advanced') {
      setActiveTab(3); // Advanced tab is index 3
    } else {
      setActiveTab(0); // Basic info tab is index 0
    }
  }, [window.location.hash]);

  // Separate function to load contacts data
  const loadContactsData = async (profileId: string) => {
    console.log('loadContactsData called with profileId:', profileId);
    try {
      const contacts = await listContacts('userprofile', profileId);
      const loadedContacts = contacts.map((contact: ContactDto) => ({
        type: contact.type || 'email',
        value: contact.value || '',
        label: contact.metadata?.label || '',
      }));
      
      setFormData(prevData => ({
        ...prevData,
        contacts: loadedContacts,
      }));
      setOriginalContacts(contacts);
      console.log('Contacts loaded successfully:', loadedContacts);
    } catch (contactError) {
      console.warn('Failed to load contacts:', contactError);
    }
  };

  const loadProfile = async (id: string) => {
    setIsLoading(true);
    try {
      const response = await api.UserprofileApi.getUserProfile(id);
      const profile = response.data.profile;
      
      // Helper function to format date from API to input format
      const formatDateFromAPI = (dateString?: string): string => {
        if (!dateString) return '';
        // Convert ISO date to YYYY-MM-DD format for input
        return dateString.split('T')[0];
      };
      
      // Load contacts for the profile
      await loadContactsData(id);

      // Update form data with loaded profile
      setFormData(prevData => ({
        ...prevData,
        first_name: profile?.first_name || '',
        middle_name: profile?.middle_name || '',
        last_name: profile?.last_name || '',
        display_name: profile?.display_name || '',
        bio: profile?.bio || '',
        date_of_birth: formatDateFromAPI(profile?.date_of_birth),
        gender: profile?.gender || '',
        location: profile?.location || '',
        language: profile?.language || 'ru',
        timezone: profile?.timezone || 'UTC',
        notification_settings: (profile as any)?.notification_settings || prevData.notification_settings,
        privacy_settings: (profile as any)?.privacy_settings || prevData.privacy_settings,
        ui_settings: (profile as any)?.ui_settings || prevData.ui_settings,
        metadata: (profile as any)?.metadata || prevData.metadata,
      }));

              // Load active avatar if available
        if (profile?.active_avatar_id) {
          try {
            console.log('Loading avatar for profile:', { id, activeAvatarId: profile.active_avatar_id });
            const response = await api.UserprofileApi.getUserAvatar(id, profile.active_avatar_id);
            const avatar = response.data.avatar;
            console.log('Avatar loaded successfully:', avatar);
            setActiveAvatar(avatar || null);
          } catch (avatarError) {
            console.warn('Failed to load avatar:', avatarError);
            // Fallback: create minimal avatar object if we have the ID
            setActiveAvatar({
              id: profile.active_avatar_id,
              is_active: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });
          }
        }
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast({
        title: t('common.error'),
        description: t('profile.errors.loadFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = t('profile.validation.firstNameRequired');
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = t('profile.validation.lastNameRequired');
    }

    if (!formData.display_name.trim()) {
      newErrors.display_name = t('profile.validation.displayNameRequired');
    }

    if (formData.bio && formData.bio.length > 500) {
      newErrors.bio = t('profile.validation.bioTooLong');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: t('common.validationError'),
        description: t('profile.validation.fixErrors'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!userId) {
      console.error('User ID not found in JWT token:', { 
        accessToken: accessToken ? 'present' : 'missing', 
        decodedToken: accessToken ? decodeJWT(accessToken) : null 
      });
      toast({
        title: t('common.error'),
        description: t('profile.errors.userNotFound'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    console.log('Starting profile save (handleSubmit) with userId:', userId);

    setIsSubmitting(true);
    try {
      // Prepare profile data for API - clean empty strings and format dates
      const formatDateForAPI = (dateString: string): string | undefined => {
        if (!dateString) return undefined;
        // Convert YYYY-MM-DD to ISO format with UTC timezone
        return new Date(dateString + 'T00:00:00.000Z').toISOString();
      };

      const profileData: UserProfileDto = {
        user_id: userId,
        first_name: formData.first_name || undefined,
        middle_name: formData.middle_name || undefined,
        last_name: formData.last_name || undefined,
        display_name: formData.display_name || undefined,
        bio: formData.bio || undefined,
        date_of_birth: formatDateForAPI(formData.date_of_birth),
        gender: formData.gender || undefined,
        location: formData.location || undefined,
        language: formData.language || 'ru',
        timezone: formData.timezone || 'UTC',
      };

      let createdProfile: UserProfileDto;

      if (isEditMode && profileId) {
        // Update existing profile
        const payload: UpdateUserProfileRequest = {
          profile: profileData
        };
        const response = await api.UserprofileApi.updateUserProfile(profileId, payload);
        if (!response.data.profile) throw new Error('Failed to update profile');
        createdProfile = response.data.profile;
        toast({
          title: t('common.success'),
          description: t('profile.basicInfoUpdated'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        console.log('Profile updated successfully via handleSubmit');
      } else {
        // Create new profile
        const response = await api.UserprofileApi.createUserProfile({
          profile: profileData
        });
        if (!response.data.profile) throw new Error('Failed to create profile');
        createdProfile = response.data.profile;
        
        toast({
          title: t('common.success'),
          description: t('profile.createSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        // Update URL to edit mode for subsequent saves
        if (response.data.profile?.id) {
          navigate(`/profile/edit/${response.data.profile.id}`, { replace: true });
          console.log('Profile created successfully via handleSubmit, navigating to edit mode');
        }
      }

      // Upload avatar if provided
      if (avatarFile && createdProfile.id) {
        try {
          await api.UserprofileApi.createAvatar(createdProfile.id, avatarFile);
          await api.UserprofileApi.setActiveAvatar(createdProfile.id, createdProfile.id); // This might need adjustment based on actual avatar ID
        } catch (avatarError) {
          console.warn('Avatar upload failed:', avatarError);
          // Don't fail the entire operation for avatar upload failure
          toast({
            title: t('common.warning'),
            description: t('profile.avatarUploadFailed'),
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        }
      }

      // Не переходим на /profiles для редактирования существующего профиля
      if (!isEditMode) {
        navigate('/profiles');
      }

    } catch (error: any) {
      console.error('Profile submit error:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('profile.errors.submitFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Separate submit handlers for each tab
  const handleBasicInfoSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: t('common.validationError'),
        description: t('profile.validation.fixErrors'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!userId) {
      console.error('User ID not found in JWT token:', { 
        accessToken: accessToken ? 'present' : 'missing', 
        decodedToken: accessToken ? decodeJWT(accessToken) : null 
      });
      toast({
        title: t('common.error'),
        description: t('profile.errors.userNotFound'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    console.log('Starting profile save with userId:', userId);

    setIsSubmitting(true);
    try {
      const formatDateForAPI = (dateString: string): string | undefined => {
        if (!dateString) return undefined;
        return new Date(dateString + 'T00:00:00.000Z').toISOString();
      };

      const profileData: UserProfileDto = {
        user_id: userId,
        first_name: formData.first_name || undefined,
        middle_name: formData.middle_name || undefined,
        last_name: formData.last_name || undefined,
        display_name: formData.display_name || undefined,
        bio: formData.bio || undefined,
        date_of_birth: formatDateForAPI(formData.date_of_birth),
        gender: formData.gender || undefined,
        location: formData.location || undefined,
        language: formData.language || 'ru',
        timezone: formData.timezone || 'UTC',
      };

      if (isEditMode && profileId) {
        const response = await api.UserprofileApi.updateUserProfile(profileId, {
          profile: profileData
        });
        toast({
          title: t('common.success'),
          description: t('profile.basicInfoUpdated'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        // Для редактирования существующего профиля не переходим на другую страницу
        console.log('Profile updated successfully');
      } else {
        const response = await api.UserprofileApi.createUserProfile({
          profile: profileData
        });
        toast({
          title: t('common.success'),
          description: t('profile.createSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        // Update URL to edit mode for subsequent saves
        if (response.data.profile?.id) {
          navigate(`/profile/edit/${response.data.profile.id}`, { replace: true });
          console.log('Profile created successfully, navigating to edit mode');
        }
      }

      // Upload avatar if provided
      if (avatarFile && (profileId || isEditMode)) {
        try {
          const targetProfileId = profileId || isEditMode;
          if (targetProfileId) {
            await api.UserprofileApi.createAvatar(targetProfileId as string, avatarFile);
          }
        } catch (avatarError) {
          console.warn('Avatar upload failed:', avatarError);
          toast({
            title: t('common.warning'),
            description: t('profile.avatarUploadFailed'),
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
        }
      }
    } catch (error: any) {
      console.error('Basic info submit error:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('profile.errors.submitFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };



  const handleSettingsSubmit = async () => {
    if (!profileId && !isEditMode) {
      toast({
        title: t('common.error'),
        description: t('profile.errors.profileNotCreated'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Update profile with settings data
      const settingsData = {
        notification_settings: formData.notification_settings,
        privacy_settings: formData.privacy_settings,
        ui_settings: formData.ui_settings,
      };

      // Since the current API might not support these fields, we'll store them as metadata
      const profileData: UserProfileDto = {
        user_id: userId,
        ...(formData as any), // Include current form data
        metadata: {
          ...formData.metadata,
          notification_settings: JSON.stringify(formData.notification_settings),
          privacy_settings: JSON.stringify(formData.privacy_settings),
          ui_settings: JSON.stringify(formData.ui_settings),
        },
      };

      if (profileId) {
        const response = await api.UserprofileApi.updateUserProfile(profileId, {
          profile: profileData
        });
        toast({
          title: t('common.success'),
          description: t('profile.settingsUpdated'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Settings submit error:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('profile.errors.settingsUpdateFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdvancedSubmit = async () => {
    if (!profileId && !isEditMode) {
      toast({
        title: t('common.error'),
        description: t('profile.errors.profileNotCreated'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const profileData: UserProfileDto = {
        user_id: userId,
        ...(formData as any),
        metadata: formData.metadata,
      };

      if (profileId) {
        const response = await api.UserprofileApi.updateUserProfile(profileId, {
          profile: profileData
        });
        toast({
          title: t('common.success'),
          description: t('profile.metadataUpdated'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error('Advanced submit error:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('profile.errors.metadataUpdateFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          title: t('common.error'),
          description: t('profile.errors.avatarTooLarge'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };



  // Metadata management
  const addMetadata = () => {
    if (!metadataKey.trim() || !metadataValue.trim()) return;

    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [metadataKey]: metadataValue
      }
    }));

    setMetadataKey('');
    setMetadataValue('');
  };

  const removeMetadata = (key: string) => {
    setFormData(prev => {
      const newMetadata = { ...prev.metadata };
      delete newMetadata[key];
      return {
        ...prev,
        metadata: newMetadata
      };
    });
  };

  // Form field handlers
  const handleInputChange = (field: keyof ProfileFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleNotificationChange = (field: keyof NotificationSettings, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      notification_settings: {
        ...prev.notification_settings,
        [field]: value
      }
    }));
  };

  const handlePrivacyChange = (field: keyof PrivacySettings, value: any) => {
    setFormData(prev => ({
      ...prev,
      privacy_settings: {
        ...prev.privacy_settings,
        [field]: value
      }
    }));
  };

  const handleUIChange = (field: keyof UISettings, value: string) => {
    setFormData(prev => ({
      ...prev,
      ui_settings: {
        ...prev.ui_settings,
        [field]: value
      }
    }));

    // Apply theme changes immediately
    if (field === 'theme') {
      if (value === 'light' && colorMode === 'dark') {
        toggleColorMode();
      } else if (value === 'dark' && colorMode === 'light') {
        toggleColorMode();
      }
      // For 'auto', we could implement system preference detection
    }
  };

  if (isLoading) {
    return (
      <Container maxW="7xl" py={8}>
        <VStack spacing={4}>
          <Spinner size="xl" colorScheme={colorSchemes.primary} />
          <Text color={"text.secondary"}>{t('common.loading')}</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="4xl" py={8} bg="bg.canvas">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box bg={"bg.surface"} p={6} borderRadius="lg" borderColor="border.default" borderWidth="1px">
          <VStack spacing={4} align="center">
            <HStack w="full" justify="space-between" align="center">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                colorScheme={colorSchemes.neutral}
                size="sm"
              >
                ← {t('common.back')}
              </Button>
              <Box /> {/* Spacer */}
            </HStack>
            
            {(() => {
              console.log('Avatar render debug:', { 
                activeAvatar, 
                avatarId: activeAvatar?.id,
                profileId,
                avatarPreview,
                hasActiveAvatar: activeAvatar && activeAvatar.id && profileId
              });
              
              // Приоритет: новый загруженный файл -> активный аватар -> пустой аватар
              if (avatarPreview) {
                return (
                  <Avatar 
                    size="2xl" 
                    src={avatarPreview} 
                    name={`${formData.first_name} ${formData.last_name}`}
                  />
                );
              }
              
              if (activeAvatar?.id && profileId) {
                return (
                  <CachedAvatar
                    avatar={activeAvatar}
                    profileId={profileId}
                    size="2xl"
                  />
                );
              }
              
              return (
                <Avatar 
                  size="2xl" 
                  name={`${formData.first_name} ${formData.last_name}`}
                />
              );
            })()}
            <VStack spacing={2} align="center">
              <Heading size="lg" color={"text.primary"}>
                {isEditMode ? t('profile.editProfile') : t('profile.createProfile')}
              </Heading>
              <Text color={"text.secondary"} textAlign="center">
                {isEditMode 
                  ? t('profile.editProfileDescription') 
                  : t('profile.createProfileDescription')
                }
              </Text>
            </VStack>
            <HStack spacing={3}>
              <Button
                leftIcon={<CameraIcon />}
                onClick={() => fileInputRef.current?.click()}
                colorScheme={colorSchemes.primary}
                variant="outline"
                size="sm"
              >
                {t('profile.uploadAvatar')}
              </Button>
              {(avatarPreview || activeAvatar) && (
                <Button
                  onClick={() => {
                    setAvatarFile(null);
                    setAvatarPreview('');
                    setActiveAvatar(null);
                  }}
                  colorScheme={colorSchemes.error}
                  variant="ghost"
                  size="sm"
                >
                  {t('common.remove')}
                </Button>
              )}
            </HStack>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              style={{ display: 'none' }}
            />
          </VStack>
        </Box>

        {/* Main Form */}
        <Box bg={"bg.surface"} borderRadius="lg" borderColor="border.default" borderWidth="1px">
          <Tabs 
            index={activeTab} 
            onChange={setActiveTab}
            variant="enclosed"
            colorScheme={colorSchemes.primary}
          >
            <TabList 
              bg={"bg.surface"} 
              borderColor="border.default"
              borderTopRadius="lg"
            >
              <Tab color={"text.secondary"} _selected={{ color: 'primary.500', bg: "bg.surface" }}>
                {t('profile.tabs.basicInfo')}
              </Tab>
              <Tab color={"text.secondary"} _selected={{ color: 'primary.500', bg: "bg.surface" }}>
                {t('profile.tabs.contacts')}
              </Tab>
              <Tab color={"text.secondary"} _selected={{ color: 'primary.500', bg: "bg.surface" }}>
                {t('profile.tabs.settings')}
              </Tab>
              <Tab color={"text.secondary"} _selected={{ color: 'primary.500', bg: "bg.surface" }}>
                {t('profile.tabs.advanced')}
              </Tab>
            </TabList>

            <TabPanels bg={"bg.surface"} p={6}>
              {/* Basic Information Tab */}
              <TabPanel px={0}>
                <VStack spacing={6} align="stretch">
                  <Alert status="info" colorScheme={colorSchemes.info} variant="subtle">
                    <AlertIcon />
                    <Text color={"text.secondary"}>{t('profile.basicInfoHelp')}</Text>
                  </Alert>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired isInvalid={!!errors.first_name}>
                      <FormLabel color={"text.primary"}>{t('profile.firstName')}</FormLabel>
                      <Input
                        value={formData.first_name}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                        placeholder={t('profile.firstNamePlaceholder')}
                        bg={"bg.input"}
                        borderColor="border.default"
                        _focus={{ borderColor: "border.accent" }}
                      />
                      <FormErrorMessage>{errors.first_name}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={"text.primary"}>{t('profile.middleName')}</FormLabel>
                      <Input
                        value={formData.middle_name}
                        onChange={(e) => handleInputChange('middle_name', e.target.value)}
                        placeholder={t('profile.middleNamePlaceholder')}
                        bg={"bg.input"}
                        borderColor="border.default"
                        _focus={{ borderColor: "border.accent" }}
                      />
                    </FormControl>

                    <FormControl isRequired isInvalid={!!errors.last_name}>
                      <FormLabel color={"text.primary"}>{t('profile.lastName')}</FormLabel>
                      <Input
                        value={formData.last_name}
                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                        placeholder={t('profile.lastNamePlaceholder')}
                        bg={"bg.input"}
                        borderColor="border.default"
                        _focus={{ borderColor: "border.accent" }}
                      />
                      <FormErrorMessage>{errors.last_name}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired isInvalid={!!errors.display_name}>
                      <FormLabel color={"text.primary"}>{t('profile.displayName')}</FormLabel>
                      <Input
                        value={formData.display_name}
                        onChange={(e) => handleInputChange('display_name', e.target.value)}
                        placeholder={t('profile.displayNamePlaceholder')}
                        bg={"bg.input"}
                        borderColor="border.default"
                        _focus={{ borderColor: "border.accent" }}
                      />
                      <FormErrorMessage>{errors.display_name}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={"text.primary"}>{t('profile.dateOfBirth')}</FormLabel>
                      <Input
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                        bg={"bg.input"}
                        borderColor="border.default"
                        _focus={{ borderColor: "border.accent" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel color={"text.primary"}>{t('profile.gender')}</FormLabel>
                      <Select
                        value={formData.gender}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                        bg={"bg.input"}
                        borderColor="border.default"
                        _focus={{ borderColor: "border.accent" }}
                      >
                        {genderOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={"text.primary"}>{t('profile.location')}</FormLabel>
                      <Input
                        value={formData.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        placeholder={t('profile.locationPlaceholder')}
                        bg={"bg.input"}
                        borderColor="border.default"
                        _focus={{ borderColor: "border.accent" }}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel color={"text.primary"}>{t('profile.language')}</FormLabel>
                      <Select
                        value={formData.language}
                        onChange={(e) => handleInputChange('language', e.target.value)}
                        bg={"bg.input"}
                        borderColor="border.default"
                        _focus={{ borderColor: "border.accent" }}
                      >
                        <option value="ru">{t('profile.languageOptions.russian')}</option>
                        <option value="en">{t('profile.languageOptions.english')}</option>
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl isInvalid={!!errors.bio}>
                    <FormLabel color={"text.primary"}>{t('profile.bio')}</FormLabel>
                    <Textarea
                      value={formData.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      placeholder={t('profile.bioPlaceholder')}
                      bg={"bg.input"}
                      borderColor="border.default"
                      _focus={{ borderColor: "border.accent" }}
                      resize="vertical"
                      minH="100px"
                    />
                    <Text fontSize="sm" color={"text.muted"} mt={1}>
                      {formData.bio.length}/500 {t('profile.characters')}
                    </Text>
                    <FormErrorMessage>{errors.bio}</FormErrorMessage>
                  </FormControl>

                  {/* Save Button for Basic Info Tab */}
                  <Box pt={4} borderTop="1px" borderColor="border.default">
                    <Button
                      type="button"
                      onClick={handleBasicInfoSubmit}
                      isLoading={isSubmitting}
                      loadingText={t('common.saving')}
                      colorScheme={colorSchemes.primary}
                      size="lg"
                      w="full"
                    >
                      {isEditMode ? t('common.save') : t('common.create')}
                    </Button>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Contacts Tab */}
              <TabPanel px={0}>
                <VStack spacing={6} align="stretch">
                  <Alert status="info" colorScheme={colorSchemes.info} variant="subtle">
                    <AlertIcon />
                    <Text color={"text.secondary"}>{t('profile.contactsHelp')}</Text>
                  </Alert>

                  {profileId && (
                    <ContactForm
                      entity={contactEntity}
                      onContactsChanged={() => {
                        // Refresh contacts data when contacts change
                        if (profileId) {
                          loadContactsData(profileId);
                        }
                      }}
                    />
                  )}

                  {!profileId && (
                    <Alert status="warning" variant="subtle">
                      <AlertIcon />
                      <Text>{t('profile.contactsAvailableAfterCreation')}</Text>
                    </Alert>
                  )}
                </VStack>
              </TabPanel>

              {/* Settings Tab */}
              <TabPanel px={0}>
                <VStack spacing={6} align="stretch">
                  {/* Notification Settings */}
                  <Box bg={"bg.surface"} p={4} borderRadius="md" border="1px" borderColor="border.default">
                    <Text fontWeight="semibold" mb={3} color={"text.primary"}>{t('profile.notificationSettings')}</Text>
                    <VStack spacing={4} align="stretch">
                      <HStack justifyContent="space-between">
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium" color={"text.primary"}>
                            {t('profile.emailNotifications')}
                          </Text>
                          <Text fontSize="xs" color={"text.muted"}>
                            {t('profile.emailNotificationsHelp')}
                          </Text>
                        </VStack>
                        <Switch
                          isChecked={formData.notification_settings.email_notifications}
                          onChange={(e) => handleNotificationChange('email_notifications', e.target.checked)}
                          colorScheme={colorSchemes.primary}
                        />
                      </HStack>

                      <HStack justifyContent="space-between">
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium" color={"text.primary"}>
                            {t('profile.pushNotifications')}
                          </Text>
                          <Text fontSize="xs" color={"text.muted"}>
                            {t('profile.pushNotificationsHelp')}
                          </Text>
                        </VStack>
                        <Switch
                          isChecked={formData.notification_settings.push_notifications}
                          onChange={(e) => handleNotificationChange('push_notifications', e.target.checked)}
                          colorScheme={colorSchemes.primary}
                        />
                      </HStack>

                      <HStack justifyContent="space-between">
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium" color={"text.primary"}>
                            {t('profile.securityAlerts')}
                          </Text>
                          <Text fontSize="xs" color={"text.muted"}>
                            {t('profile.securityAlertsHelp')}
                          </Text>
                        </VStack>
                        <Switch
                          isChecked={formData.notification_settings.security_alerts}
                          onChange={(e) => handleNotificationChange('security_alerts', e.target.checked)}
                          colorScheme={colorSchemes.primary}
                        />
                      </HStack>

                      <HStack justifyContent="space-between">
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium" color={"text.primary"}>
                            {t('profile.marketingEmails')}
                          </Text>
                          <Text fontSize="xs" color={"text.muted"}>
                            {t('profile.marketingEmailsHelp')}
                          </Text>
                        </VStack>
                        <Switch
                          isChecked={formData.notification_settings.marketing_emails}
                          onChange={(e) => handleNotificationChange('marketing_emails', e.target.checked)}
                          colorScheme={colorSchemes.primary}
                        />
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Privacy Settings */}
                  <Box bg={"bg.surface"} p={4} borderRadius="md" border="1px" borderColor="border.default">
                    <Text fontWeight="semibold" mb={3} color={"text.primary"}>{t('profile.privacySettings')}</Text>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel fontSize="sm" color={"text.primary"}>{t('profile.profileVisibility')}</FormLabel>
                        <Select
                          value={formData.privacy_settings.profile_visibility}
                          onChange={(e) => handlePrivacyChange('profile_visibility', e.target.value)}
                          bg={"bg.input"}
                          borderColor="border.default"
                          size="sm"
                        >
                          {privacyOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <HStack justifyContent="space-between">
                        <Text fontSize="sm" fontWeight="medium" color={"text.primary"}>
                          {t('profile.showEmail')}
                        </Text>
                        <Switch
                          isChecked={formData.privacy_settings.show_email}
                          onChange={(e) => handlePrivacyChange('show_email', e.target.checked)}
                          colorScheme={colorSchemes.primary}
                        />
                      </HStack>

                      <HStack justifyContent="space-between">
                        <Text fontSize="sm" fontWeight="medium" color={"text.primary"}>
                          {t('profile.showLocation')}
                        </Text>
                        <Switch
                          isChecked={formData.privacy_settings.show_location}
                          onChange={(e) => handlePrivacyChange('show_location', e.target.checked)}
                          colorScheme={colorSchemes.primary}
                        />
                      </HStack>

                      <HStack justifyContent="space-between">
                        <Text fontSize="sm" fontWeight="medium" color={"text.primary"}>
                          {t('profile.showBirthDate')}
                        </Text>
                        <Switch
                          isChecked={formData.privacy_settings.show_birth_date}
                          onChange={(e) => handlePrivacyChange('show_birth_date', e.target.checked)}
                          colorScheme={colorSchemes.primary}
                        />
                      </HStack>
                    </VStack>
                  </Box>

                  {/* UI Settings */}
                  <Box bg={"bg.surface"} p={4} borderRadius="md" border="1px" borderColor="border.default">
                    <Text fontWeight="semibold" mb={3} color={"text.primary"}>{t('profile.uiSettings')}</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm" color={"text.primary"}>{t('profile.theme')}</FormLabel>
                        <Select
                          value={formData.ui_settings.theme}
                          onChange={(e) => handleUIChange('theme', e.target.value)}
                          bg={"bg.input"}
                          borderColor="border.default"
                          size="sm"
                        >
                          <option value="auto">{t('profile.themeOptions.auto')}</option>
                          <option value="light">{t('profile.themeOptions.light')}</option>
                          <option value="dark">{t('profile.themeOptions.dark')}</option>
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm" color={"text.primary"}>{t('profile.timezone')}</FormLabel>
                        <Select
                          value={formData.ui_settings.timezone}
                          onChange={(e) => handleUIChange('timezone', e.target.value)}
                          bg={"bg.input"}
                          borderColor="border.default"
                          size="sm"
                        >
                          <option value="UTC">UTC</option>
                          <option value="Europe/Moscow">Moscow (UTC+3)</option>
                          <option value="Europe/London">London (UTC+0)</option>
                          <option value="America/New_York">New York (UTC-5)</option>
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm" color={"text.primary"}>{t('profile.dateFormat')}</FormLabel>
                        <Select
                          value={formData.ui_settings.date_format}
                          onChange={(e) => handleUIChange('date_format', e.target.value)}
                          bg={"bg.input"}
                          borderColor="border.default"
                          size="sm"
                        >
                          <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm" color={"text.primary"}>{t('profile.timeFormat')}</FormLabel>
                        <Select
                          value={formData.ui_settings.time_format}
                          onChange={(e) => handleUIChange('time_format', e.target.value)}
                          bg={"bg.input"}
                          borderColor="border.default"
                          size="sm"
                        >
                          <option value="24h">{t('profile.timeFormatOptions.24h')}</option>
                          <option value="12h">{t('profile.timeFormatOptions.12h')}</option>
                        </Select>
                      </FormControl>
                    </SimpleGrid>
                  </Box>

                  {/* Save Button for Settings Tab */}
                  <Box pt={4} borderTop="1px" borderColor="border.default">
                    <Button
                      type="button"
                      onClick={handleSettingsSubmit}
                      isLoading={isSubmitting}
                      loadingText={t('common.saving')}
                      colorScheme={colorSchemes.primary}
                      size="lg"
                      w="full"
                    >
                      {isEditMode ? t('common.save') : t('common.create')}
                    </Button>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Advanced Tab */}
              <TabPanel px={0}>
                <VStack spacing={6} align="stretch">
                  {/* Metadata Section */}
                  <Accordion allowToggle>
                    <AccordionItem border="1px" borderColor="border.default" borderRadius="md">
                      <AccordionButton bg={"bg.surface"} _expanded={{ bg: "bg.surface" }}>
                        <Box flex="1" textAlign="left">
                          <Text fontWeight="semibold" color={"text.primary"}>{t('profile.metadata')}</Text>
                          <Text fontSize="sm" color={"text.muted"}>{t('profile.metadataHelp')}</Text>
                        </Box>
                        <AccordionIcon color={"text.secondary"} />
                      </AccordionButton>
                      <AccordionPanel pb={4} bg={"bg.surface"}>
                        <VStack spacing={4} align="stretch">
                          <HStack>
                            <Input
                              placeholder={t('profile.metadataKey')}
                              value={metadataKey}
                              onChange={(e) => setMetadataKey(e.target.value)}
                              flex={1}
                              bg={"bg.input"}
                              borderColor="border.default"
                            />
                            <Input
                              placeholder={t('profile.metadataValue')}
                              value={metadataValue}
                              onChange={(e) => setMetadataValue(e.target.value)}
                              flex={1}
                              bg={"bg.input"}
                              borderColor="border.default"
                            />
                            <IconButton
                              aria-label={t('common.add')}
                              icon={<AddIcon />}
                              onClick={addMetadata}
                              isDisabled={!metadataKey.trim() || !metadataValue.trim()}
                              colorScheme={colorSchemes.primary}
                              variant="outline"
                            />
                          </HStack>

                          {Object.keys(formData.metadata).length > 0 && (
                            <Box>
                              <Text fontSize="sm" fontWeight="medium" mb={2} color={"text.primary"}>
                                {t('profile.currentMetadata')}
                              </Text>
                              <Wrap>
                                {Object.entries(formData.metadata).map(([key, value]) => (
                                  <WrapItem key={key}>
                                    <Tag size="md" colorScheme={colorSchemes.primary} variant="subtle">
                                      <TagLabel>{key}: {value}</TagLabel>
                                      <TagCloseButton onClick={() => removeMetadata(key)} />
                                    </Tag>
                                  </WrapItem>
                                ))}
                              </Wrap>
                            </Box>
                          )}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>

                  <Alert status="warning" colorScheme={colorSchemes.warning} variant="subtle">
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="medium" color={"text.primary"}>
                        {t('profile.advancedWarning')}
                      </Text>
                      <Text fontSize="xs" color={"text.secondary"}>
                        {t('profile.advancedWarningText')}
                      </Text>
                    </VStack>
                  </Alert>

                  {/* Save Button for Advanced Tab */}
                  <Box pt={4} borderTop="1px" borderColor="border.default">
                    <Button
                      type="button"
                      onClick={handleAdvancedSubmit}
                      isLoading={isSubmitting}
                      loadingText={t('common.saving')}
                      colorScheme={colorSchemes.primary}
                      size="lg"
                      w="full"
                    >
                      {isEditMode ? t('common.save') : t('common.create')}
                    </Button>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>
    </Container>
  );
}; 